// src/scenes/game-components/SpawningManager.js
// 🎯 SpawningManager - FIXED: NPCs properly positioned on ground and moving horizontally
// Manages timing, positioning, and cleanup of all spawned objects

import Phaser from 'phaser';
import { gameState } from '../../game/state.js';
import { GAME_CONFIG } from '../../game/config.js';

export default class SpawningManager {
  constructor(scene) {
    this.scene = scene;
    this.worldSpeed = scene.getWorldSpeed();
    this.spawnTimers = {};
  }

  startSpawning() {
    // Spawn platforms more accessibly
    this.spawnTimers.platforms = this.scene.time.addEvent({
      delay: GAME_CONFIG.SPAWN.PLATFORMS,
      callback: this.spawnAccessiblePlatform,
      callbackScope: this,
      loop: true
    });
    
    // Spawn collectibles
    this.spawnTimers.items = this.scene.time.addEvent({
      delay: GAME_CONFIG.SPAWN.ITEMS,
      callback: this.spawnCollectible,
      callbackScope: this,
      loop: true
    });
    
    // Spawn heart boxes
    this.spawnTimers.heartBoxes = this.scene.time.addEvent({
      delay: 6000,
      callback: this.spawnHeartBox,
      callbackScope: this,
      loop: true
    });
    
    // Spawn obstacles
    this.spawnTimers.obstacles = this.scene.time.addEvent({
      delay: GAME_CONFIG.SPAWN.OBSTACLES,
      callback: this.spawnObstacle,
      callbackScope: this,
      loop: true
    });
    
    // Spawn moving NPCs
    this.spawnTimers.npcs = this.scene.time.addEvent({
      delay: GAME_CONFIG.SPAWN.NPCS,
      callback: this.spawnMovingNPC,
      callbackScope: this,
      loop: true
    });
    
    // Initial spawns
    this.scene.time.delayedCall(1000, () => this.spawnCollectible());
    this.scene.time.delayedCall(2000, () => this.spawnAccessiblePlatform());
    this.scene.time.delayedCall(3000, () => this.spawnHeartBox());
    
    /* console.log('🎯 Fixed spawning systems started with ground-walking NPCs'); */
  }

  stopSpawning() {
    Object.values(this.spawnTimers).forEach(timer => {
      if (timer) timer.destroy();
    });
    this.spawnTimers = {};
    /* console.log('⏹️ Spawning stopped'); */
  }

  updateWorldSpeed(newSpeed) {
    this.worldSpeed = newSpeed;
  }

  spawnAccessiblePlatform() {
    const { width, height } = this.scene.scale;
    const physicsGroups = this.scene.getPhysicsGroups();
    const player = this.scene.getPlayer();
    
    // FIXED: Position platforms above ground (ground physics at height - 30)
    const groundLevel = height - 30;
    const x = player.x + width + Phaser.Math.Between(200, 400);
    const y = groundLevel - Phaser.Math.Between(60, 120); // Proper platform height
    
    let platformKey;
    switch (gameState.level) {
      case 1:
        platformKey = 'wood-platform';
        break;
      case 2:
        platformKey = 'stone-platform';
        break;
      case 3:
        platformKey = 'disco-platform';
        break;
      default:
        platformKey = 'grass-platform';
    }
    
    const platformSprite = this.scene.add.image(x, y, platformKey);
    // FIXED: Thinner platforms
    const platformScale = GAME_CONFIG.SPRITES.SCALE.PLATFORMS * 0.7; // Make platforms thinner
    platformSprite.setScale(platformScale, platformScale * 0.5); // Width normal, height reduced
    
    this.scene.physics.add.existing(platformSprite, true);
    // FIXED: Collision box adjusted for thinner platforms
    const collisionWidth = GAME_CONFIG.SPRITES.PLATFORM_WIDTH * platformScale;
    const collisionHeight = GAME_CONFIG.SPRITES.PLATFORM_HEIGHT * platformScale * 0.5;
    platformSprite.body.setSize(collisionWidth, collisionHeight);
    physicsGroups.platforms.add(platformSprite);
    
    this.scene.physics.add.collider(player, platformSprite);
    
    this.scene.time.delayedCall(20000, () => {
      if (platformSprite && platformSprite.active) {
        platformSprite.destroy();
      }
    });
    
    /* console.log(`🏗️ Spawned platform (${platformKey}) at ${x}, ${y} with scale ${platformScale}`); */
  }

  spawnCollectible() {
    const { width, height } = this.scene.scale;
    const physicsGroups = this.scene.getPhysicsGroups();
    const player = this.scene.getPlayer();
    
    const collectibleTypes = {
      1: ['ring', 'heart-coin', 'bouquet'],
      2: ['ring', 'champagne', 'heart-coin'],
      3: ['cake-slice', 'gift', 'star']
    };
    
    const availableTypes = collectibleTypes[gameState.level] || collectibleTypes[1];
    const itemType = Phaser.Utils.Array.GetRandom(availableTypes);
    
    // FIXED: Position collectibles more dynamically - higher up and more varied
    const groundLevel = height - 30;
    const x = player.x + width + 50;
    const y = groundLevel - 80 - Math.random() * 120; // Much higher and more varied (80-200 pixels above ground)
    
    // FIXED: Create as physics sprite but disable gravity
    const item = this.scene.physics.add.sprite(x, y, itemType);
    const itemScale = GAME_CONFIG.SPRITES.SCALE.ITEMS;
    item.setScale(itemScale);
    
    // FIXED: Disable gravity and movement to prevent falling
    item.body.setGravityY(0);
    item.body.setVelocity(0, 0);
    item.body.setImmovable(true);
    item.body.setSize(16, 16);
    
    item.itemType = itemType;
    item.points = this.getItemPoints(itemType);
    
    // Enhanced floating animation - more dynamic movement
    this.scene.tweens.add({
      targets: item,
      y: y - 20,
      duration: 2000 + Math.random() * 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Enhanced sparkle animation
    this.scene.tweens.add({
      targets: item,
      scaleX: itemScale * 1.05,
      scaleY: itemScale * 1.05,
      duration: 1000,
      yoyo: true,
      repeat: -1
    });
    
    physicsGroups.collectibles.add(item);
    
    this.scene.time.delayedCall(15000, () => {
      if (item && item.active) {
        item.destroy();
      }
    });
    
    /* console.log(`💰 Spawned collectible ${itemType} at ${x}, ${y} with scale ${itemScale}`); */
  }

  spawnMovingNPC() {
    const { width, height } = this.scene.scale;
    const physicsGroups = this.scene.getPhysicsGroups();
    const player = this.scene.getPlayer();
    
    let npcConfig;
    
    // Different NPCs per level with distinct sprites
    switch (gameState.level) {
      case 1: // LEVEL 1: Only salespeople (bigger, distinct sprites)
        const salespersonTypes = [
          { type: 'salesperson-blue', scale: GAME_CONFIG.SPRITES.SCALE.NPCS * 1.3, name: 'SHOP CLERK' },
          { type: 'salesperson-red', scale: GAME_CONFIG.SPRITES.SCALE.NPCS * 1.3, name: 'SALES REP' }
        ];
        npcConfig = Phaser.Utils.Array.GetRandom(salespersonTypes);
        break;
        
      case 2: // LEVEL 2: Cats + waiters + smaller Miles
        const level2NPCs = [
          { type: 'nova-cat', scale: GAME_CONFIG.SPRITES.SCALE.NPCS, name: 'NOVA CAT' },
          { type: 'linus-cat', scale: GAME_CONFIG.SPRITES.SCALE.NPCS, name: 'LINUS CAT' },
          { type: 'waiter-white', scale: GAME_CONFIG.SPRITES.SCALE.NPCS, name: 'WAITER' },
          { type: 'waiter-black', scale: GAME_CONFIG.SPRITES.SCALE.NPCS, name: 'WAITRESS' },
          { type: 'miles', scale: GAME_CONFIG.SPRITES.SCALE.NPCS * 0.8, name: 'MILES' } // Smaller Miles
        ];
        npcConfig = Phaser.Utils.Array.GetRandom(level2NPCs);
        break;
        
      case 3: // LEVEL 3: All NPCs
        const allNPCs = [
          { type: 'nova-cat', scale: GAME_CONFIG.SPRITES.SCALE.NPCS, name: 'NOVA CAT' },
          { type: 'linus-cat', scale: GAME_CONFIG.SPRITES.SCALE.NPCS, name: 'LINUS CAT' },
          { type: 'salesperson-blue', scale: GAME_CONFIG.SPRITES.SCALE.NPCS * 1.3, name: 'SHOP CLERK' },
          { type: 'salesperson-red', scale: GAME_CONFIG.SPRITES.SCALE.NPCS * 1.3, name: 'SALES REP' },
          { type: 'waiter-white', scale: GAME_CONFIG.SPRITES.SCALE.NPCS, name: 'WAITER' },
          { type: 'waiter-black', scale: GAME_CONFIG.SPRITES.SCALE.NPCS, name: 'WAITRESS' },
          { type: 'miles', scale: GAME_CONFIG.SPRITES.SCALE.NPCS, name: 'MILES' } // Normal Miles
        ];
        npcConfig = Phaser.Utils.Array.GetRandom(allNPCs);
        break;
    }
    
    // Position NPCs properly on ground level
    const groundLevel = height - 30;
    const x = player.x + width * 0.8;
    
    let y;
    if (npcConfig.type.includes('cat')) {
      y = groundLevel - 20; // Cats are shorter
    } else {
      y = groundLevel - 40; // All humanoid NPCs are taller
    }
    
    // Create NPC using its specific sprite (no overlays needed)
    const npc = this.scene.physics.add.sprite(x, y, npcConfig.type);
    npc.setScale(npcConfig.scale);
    
    // Physics setup
    npc.body.setGravityY(GAME_CONFIG.PHYSICS.GRAVITY);
    npc.body.setVelocityX(-this.worldSpeed * 0.4); // Move left with world
    npc.body.setDragX(0); // No drag for smooth movement
    npc.body.setBounce(0); // No bouncing
    npc.npcType = npcConfig.type;
    
    // Collision sizes based on NPC type
    if (npcConfig.type.includes('cat')) {
      npc.body.setSize(24, 16); // Cat collision box
    } else {
      npc.body.setSize(20, 32); // Humanoid collision box
    }
    
    // Add collision with ground so NPCs walk on surface
    this.scene.physics.add.collider(npc, physicsGroups.ground);
    
    // Create name label positioned above NPC
    const nameLabel = this.scene.add.text(x, y - 35, npcConfig.name, {
      fontSize: '8px',
      fontFamily: 'Press Start 2P',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 1,
      backgroundColor: 'rgba(0,0,0,0.7)',
      padding: { x: 3, y: 2 }
    }).setOrigin(0.5);
    
    // Make label follow NPC smoothly
    const updateLabel = () => {
      if (npc.active && nameLabel.active) {
        nameLabel.x = npc.x;
        nameLabel.y = npc.y - 35;
      }
    };
    
    // Update label position each frame
    this.scene.time.addEvent({
      delay: 16, // 60 FPS
      callback: updateLabel,
      repeat: 1500 // For 25 seconds
    });
    
    // Walking animation based on NPC type
    if (npcConfig.type.includes('cat')) {
      // Cats get slight head bob while walking
      this.scene.tweens.add({
        targets: npc,
        y: y - 3,
        duration: 400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    } else {
      // Humanoid NPCs get slight bounce while walking
      this.scene.tweens.add({
        targets: npc,
        y: y - 5,
        duration: 600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
    
    // Add to physics group
    physicsGroups.npcs.add(npc);
    
    // Auto cleanup after 25 seconds
    this.scene.time.delayedCall(25000, () => {
      if (npc && npc.active) {
        npc.destroy();
      }
      if (nameLabel && nameLabel.active) {
        nameLabel.destroy();
      }
    });
    
    console.log(`🧑 Spawned ${npcConfig.type} at level ${gameState.level} with scale ${npcConfig.scale}`);
  }

  getNPCLabel(npcType) {
    const labels = {
      'nova-cat': 'NOVA CAT',
      'linus-cat': 'LINUS CAT',
      'miles': 'MILES',
      'salesperson-blue': 'SHOP CLERK',
      'salesperson-red': 'SALES REP',
      'salesperson-green': 'MANAGER',
      'salesperson-purple': 'ASSISTANT',
      'waiter-white': 'WAITER',
      'waiter-black': 'WAITER'
    };
    
    return labels[npcType] || npcType.toUpperCase();
  }

  spawnHeartBox() {
    const { width, height } = this.scene.scale;
    const physicsGroups = this.scene.getPhysicsGroups();
    const player = this.scene.getPlayer();
    
    // FIXED: Position question boxes above ground (ground physics at height - 30)
    const groundLevel = height - 30;
    const x = player.x + width + 100;
    const y = groundLevel - 120; // Well above ground
    
    // Check for platform collision to avoid overlap
    let adjustedY = y;
    const platformCheckY = groundLevel - 140;
    if (Math.abs(y - platformCheckY) < 50) {
      adjustedY = platformCheckY - 80;
    }
    
    // Create question box texture if it doesn't exist
    if (!this.scene.textures.exists('question-box')) {
      this.createQuestionBoxTexture();
    }
    
    // FIXED: Create as physics sprite for proper collision - SAME AS COLLECTIBLES
    const questionBox = this.scene.physics.add.sprite(x, adjustedY, 'question-box');
    
    // FIXED: Same setup as collectible items
    const boxScale = GAME_CONFIG.SPRITES.SCALE.HEART_BOX;
    questionBox.setScale(boxScale);
    questionBox.body.setGravityY(0); // Float in air like collectibles
    questionBox.body.setVelocity(0, 0);
    questionBox.body.setImmovable(true);
    questionBox.body.setSize(32, 32); // Same collision box as items
    
    // Add custom properties - SAME AS COLLECTIBLES
    questionBox.itemType = 'question-box'; // Treat as special collectible
    questionBox.points = 500; // Base points value
    questionBox.boxType = 'question';
    questionBox.isUsed = false;
    
    // DEBUG: Log question box creation
    /* console.log('📦 Creating question box as collectible with properties:', {
      x: questionBox.x,
      y: questionBox.y,
      itemType: questionBox.itemType,
      points: questionBox.points,
      hasBody: !!questionBox.body
    }); */
    
    // CHANGED: Add to collectibles group instead of heartBoxes for same collision system
    physicsGroups.collectibles.add(questionBox);
   /*  console.log('📦 Added question box to collectibles group for reliable collision'); */
    
    // Enhanced floating animation - SAME AS COLLECTIBLES
    this.scene.tweens.add({
      targets: questionBox,
      y: adjustedY - 20,
      duration: 2000 + Math.random() * 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
    
    // Enhanced sparkle animation - SAME AS COLLECTIBLES
    this.scene.tweens.add({
      targets: questionBox,
      scaleX: boxScale * 1.05,
      scaleY: boxScale * 1.05,
      duration: 1000,
      yoyo: true,
      repeat: -1
    });
    
    // Auto cleanup
    this.scene.time.delayedCall(15000, () => {
      if (questionBox && questionBox.active) {
       /*  console.log('📦 Auto-cleaning up question box'); */
        questionBox.destroy();
      }
    });
    
    /* console.log(`📦 Successfully spawned question box as collectible at ${x}, ${adjustedY}`); */
  }

  // Create question box texture that can be used as a sprite
  createQuestionBoxTexture() {
    const size = 32;
    const graphics = this.scene.add.graphics();
    
    // Box with golden outline and 3D effect
    graphics.fillStyle(0xFFD700); // Gold
    graphics.fillRect(0, 0, size, size);
    
    graphics.fillStyle(0x8B4513); // Brown interior
    graphics.fillRect(2, 2, size - 4, size - 4);
    
    // 3D shading - highlights
    graphics.fillStyle(0xFFF8DC); // Light highlight
    graphics.fillRect(2, 2, size - 4, 2); // Top
    graphics.fillRect(2, 2, 2, size - 4); // Left
    
    // 3D shading - shadows
    graphics.fillStyle(0xDAA520); // Dark shadow
    graphics.fillRect(2, size - 4, size - 4, 2); // Bottom
    graphics.fillRect(size - 4, 2, 2, size - 4); // Right
    
    // Question mark - larger and clearer
    graphics.fillStyle(0xFFFFFF); // White question mark
    
    // Question mark
    graphics.fillStyle(0xFFFFFF);
    graphics.fillRect(10, 6, 12, 4);
    graphics.fillRect(18, 10, 4, 8);
    graphics.fillRect(14, 18, 4, 4);
    graphics.fillRect(14, 24, 4, 4);
    
    // Generate texture and clean up
    graphics.generateTexture('question-box', size, size);
    graphics.destroy();
    
    /* console.log('📦 Question box texture created'); */
  }

  spawnObstacle() {
    const { width, height } = this.scene.scale;
    const physicsGroups = this.scene.getPhysicsGroups();
    const player = this.scene.getPlayer();
    
    // FIXED: Position obstacles properly above ground (ground physics at height - 30)
    const groundLevel = height - 30;
    const x = player.x + width;
    const y = groundLevel - 40; // Just above ground level
    
    // Choose realistic obstacle based on level
    const obstacleTypes = {
      1: ['falling-cake', 'champagne-cork', 'flying-bouquet'],
      2: ['rice-shower', 'wedding-guest', 'flying-bouquet'],
      3: ['wine-spill', 'champagne-cork', 'wedding-guest']
    };
    
    const availableTypes = obstacleTypes[gameState.level] || obstacleTypes[1];
    const obstacleType = Phaser.Utils.Array.GetRandom(availableTypes);
    
    // Create realistic obstacle sprite
    const obstacle = this.scene.add.image(x, y, obstacleType);
    // FIXED: Use config scaling for obstacles
    const obstacleScale = GAME_CONFIG.SPRITES.SCALE.ITEMS;
    obstacle.setScale(obstacleScale);
    
    // Add physics
    this.scene.physics.add.existing(obstacle);
    obstacle.body.setVelocityX(-this.worldSpeed * 1.5);
    obstacle.body.setImmovable(true);
    // FIXED: Proper collision box
    obstacle.body.setSize(16, 16); // Fixed collision box
    
    obstacle.obstacleType = obstacleType;
    
    // Special movement patterns
    if (obstacleType === 'flying-bouquet') {
      obstacle.body.setVelocityY(-50);
      this.scene.tweens.add({
        targets: obstacle,
        rotation: Math.PI * 2,
        duration: 2000,
        repeat: -1
      });
    } else if (obstacleType === 'falling-cake') {
      obstacle.body.setVelocityY(100);
      this.scene.tweens.add({
        targets: obstacle,
        rotation: 0.5,
        duration: 1000,
        yoyo: true,
        repeat: -1
      });
    } else if (obstacleType === 'rice-shower') {
      obstacle.body.setVelocityY(80);
    }
    
    physicsGroups.obstacles.add(obstacle);
    
    // Auto cleanup
    this.scene.time.delayedCall(10000, () => {
      if (obstacle && obstacle.active) {
        obstacle.destroy();
      }
    });
    
    /* console.log(`🚧 Spawned obstacle: ${obstacleType} at ${x}, ${y} with scale ${obstacleScale}`); */
  }

  getItemPoints(itemType) {
    const pointValues = {
      'ring': 100,
      'heart-coin': 200,
      'bouquet': 150,
      'champagne': 150,
      'cake-slice': 200,
      'gift': 250,
      'star': 500
    };
    
    return pointValues[itemType] || 100;
  }
}